import { CancelTokenSource } from 'axios';
import { getAccessToken } from 'services/browserStorageServices';
import {
  AutomaticBillingProgressDto,
  AutomaticBillingRequest,
  AutomaticBillingResponse,
  AutomaticBillingSearchRequest,
  AutomaticBillingSearchResponseWrapper,
  AutomaticBillRunDto,
  AutomaticBillRunPdfResponse,
  AutomaticReminderRequest,
  AutomaticReminderResponse,
  BillCreationRequest,
  BillCreationResponse,
  BillDto,
  BillingDataDto,
  BillPriceRequest
} from 'ts-types/api.types';
import axios from './http';

const {REACT_APP_API_BASE_URL: baseUrl} = process.env;
const apiUri = `${baseUrl}/api/bills`;

export const getAllBillsByTreatmentId = (treatmentId: number, source: CancelTokenSource): Promise<Array<BillDto>> => {
  return axios
      .get(`${apiUri}/${treatmentId}`, {cancelToken: source.token})
      .then(response => response.data);
};

export const getBillingDataForTreatment = (treatmentId: number, source: CancelTokenSource): Promise<BillingDataDto> => {
  return axios
      .get(`${apiUri}/treatment-billing-data?treatmentId=${treatmentId}`, {cancelToken: source.token})
      .then(response => response.data);
};

export const reCalculateBillPrice = (billPriceRequest: BillPriceRequest, source: CancelTokenSource): Promise<number> => {
  return axios
      .post(`${apiUri}/recalculate-bill-price`, billPriceRequest, {cancelToken: source.token})
      .then(response => response.data);
};

export const getTreatmentsForAutomaticBilling = (
    searchRequest: Partial<AutomaticBillingSearchRequest>,
    source: CancelTokenSource)
    : Promise<AutomaticBillingSearchResponseWrapper> => {

  const nbfFix = sessionStorage.getItem("fix-NBF");
  searchRequest.fixOnly = nbfFix != null && nbfFix === "true";

  return axios
      .post(`${apiUri}/automatic-billing/treatments`, searchRequest, {cancelToken: source.token})
      .then(response => response.data);
};

export const getAutomaticBillRunPdfData = (
    runId: number, source: CancelTokenSource)
    : Promise<AutomaticBillRunPdfResponse> => {

  return axios
      .get(`${apiUri}/automatic-billing/run-data/${runId}/pdf`, {cancelToken: source.token})
      .then(response => response.data);
}

export const billTreatment = (billCreationRequest: Partial<BillCreationRequest>, source: CancelTokenSource): Promise<BillCreationResponse> => {
  return axios
      .post(`${apiUri}/bill-treatment`, billCreationRequest, {cancelToken: source.token})
      .then(response => response.data)
};

export const billTreatments = (
    automaticBillingRequest: Partial<AutomaticBillingRequest>,
    source: CancelTokenSource)
    : Promise<AutomaticBillingProgressDto> => {

  return axios
      .post(
          `${apiUri}/automatic-billing/bill-treatments`,
          automaticBillingRequest,
          {
            cancelToken: source.token,
            timeout: 10 * 60 * 1000
          })
      .then(response => response.data);
};

export const getAutomaticBillExecutionStatus = (
    runId: string, source: CancelTokenSource)
    : Promise<AutomaticBillingProgressDto> => {
  return axios
      .get(`${apiUri}/automatic-billing/${runId}/status`, {cancelToken: source.token})
      .then(response => response.data);
};

export const getAutomaticBillExecutionResult = (
    runId: string, source: CancelTokenSource)
    : Promise<AutomaticBillingResponse> => {
  return axios
      .get(`${apiUri}/automatic-billing/${runId}/result`, {cancelToken: source.token})
      .then(response => response.data);
};

export const getAutomaticBillingExportUrl = (runId: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("upn", getAccessToken() ? getAccessToken() + "" : "");
  const url = `${apiUri}/automatic-billing/${runId}/excel-export?${searchParams.toString()}`;
  return url;
};

export const getAutomaticBillRunData = (
    mandateId: number,
    source: CancelTokenSource): Promise<Array<AutomaticBillRunDto>> => {

  return axios
      .get(`${apiUri}/automatic-billing/run-data`,
          {params: {mandateId}, cancelToken: source.token})
      .then(response => response.data);
};

export const getAutomaticBillRunById = (runId: number, source: CancelTokenSource): Promise<AutomaticBillRunDto> => {
  return axios
      .get(`${apiUri}/automatic-billing/run-data/${runId}`, {cancelToken: source.token})
      .then(response => response.data);
};

export const getBillPdfUrl = (billPdfId: string, billPdfName: string) => {
  return `${baseUrl}/6f3e057be40e4a3981837c929f802105/${billPdfId}/${billPdfName}`;
};

export const getAutomaticBillingPdfUrl = (billPdfId: string, billPdfName: string) => {
  return `${baseUrl}/0f8df82031944d5086ca400c0625f2bf/${billPdfId}/${billPdfName}`;
};

export const getAutomaticBillRunPdfUrl = (billPdfId: string, billPdfName: string) => {
  return `${baseUrl}/b40728a8121511ebadc10242ac120002/${billPdfId}/${billPdfName}`;
};

export const getAutomaticBillingExcelUrl = (excelId: string, excelName: string) => {
  return `${baseUrl}/314057f9f99849d488b249e4df4568b2/${excelId}/${excelName}`;
};

export const getUnpaidBills = (request: Partial<AutomaticReminderRequest>,
                               source: CancelTokenSource): Promise<Array<AutomaticReminderResponse>> => {
  return axios.post(`${apiUri}/unpaid-bills`, request, {cancelToken: source.token})
      .then(response => response.data);
};
